import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Edit, Trash2, Loader2, Plus, RefreshCw } from "lucide-react";
import { notify } from "@/lib/utils/toast";
import { usersApi, User as ApiUser, UserFilters, CreateUserRequest, UpdateUserRequest } from "../lib/api/users";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PERMISSIONS, useAuthStore } from "@/lib";
import { NgocInput } from "./ui/ngocInput";
import { UserModal } from "./modals/UserModal";
import { UserSchemaType } from "./validations/user";
import { PaginationIndex } from "./PaginationIndex";
import { SmoothLoadingWrapper } from "./animate/SmoothLoadingWrapper";

export default function UserManagement() {
	const { hasPermission } = useAuthStore()
	const [users, setUsers] = useState<ApiUser[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [roleFilter, setRoleFilter] = useState("all");
	const [statusFilter, setStatusFilter] = useState("all");
	const [editUserData, setEditUserData] = useState<ApiUser | null>(null);

	// Pagination
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
	const [itemsPerPage, setItemsPerPage] = useState(10);


	const [handleMode, setHandleMode] = useState<"create" | "edit">("create");

	const canCreate = hasPermission(PERMISSIONS.users.permissions.create.key);
	const canUpdate = hasPermission(PERMISSIONS.users.permissions.update.key);
	const canDelete = hasPermission(PERMISSIONS.users.permissions.delete.key);

	const fetchList = useCallback(async () => {
		try {
			setLoading(true);
			const filters: UserFilters = {
				search: searchTerm || undefined,
				role: roleFilter !== "all" ? (roleFilter as any) : undefined,
				status: statusFilter !== "all" ? (statusFilter as any) : undefined,
				page: currentPage,
				limit: itemsPerPage,
			};

			const response = await usersApi.getAll(filters);
			if (response.success && response.data) {
				setUsers(response.data.data);
				setTotalPages(response.data.totalPages);
				setTotalCount(response.data.total);
			}
		} catch (error) {
			console.error("Error loading users:", error);
			notify.error("Lỗi khi tải dữ liệu người dùng");
		} finally {
			setLoading(false);
		}
	}, [searchTerm, roleFilter, statusFilter, currentPage, itemsPerPage]);

	useEffect(() => {
		fetchList();
	}, [fetchList]);

	const handleFilterChange = (setter: Function, value: any) => {
		setter(value);
		setCurrentPage(1);
	};

	const handleAdd = () => {
		setHandleMode("create");
		setIsDialogOpen(true);
	};

	const handleUpdate = (user: ApiUser) => {
		setEditUserData(user);
		setHandleMode("edit");
		setIsDialogOpen(true);
	};

	const handleDelete = async () => {
		try {
			if (deleteItemId) {
				const response = await usersApi.delete(deleteItemId);
				if (response.success) {
					notify.success("Đã xóa người dùng thành công");
					await fetchList();
				}
			}
			setDeleteItemId(null);
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error!.message);
			}
		}
	};

	// const handleStatusToggle = async (id: string, currentStatus: string) => {
	// 	try {
	// 		const newStatus = currentStatus === "active" ? "inactive" : "active";
	// 		const response = await usersApi.update(id, { status: newStatus });
	// 		if (response.success) {
	// 			notify.success(`Đã ${newStatus === "active" ? "kích hoạt" : "vô hiệu hóa"} người dùng`);
	// 			await loadUsers(); // Reload data
	// 		} else {
	// 			notify.error(response.message || "Lỗi khi cập nhật trạng thái");
	// 		}
	// 	} catch (error) {
	// 		notify.error("Lỗi khi cập nhật trạng thái");
	// 	}
	// };

	const updateUser = async (updateUserData: UserSchemaType) => {
		try {
			if (updateUserData.id) {
				const updateData: UpdateUserRequest = {
					id: updateUserData.id,
					name: updateUserData.name,
					email: updateUserData.email,
					username: updateUserData.username || undefined,
					role: updateUserData.role,
					status: updateUserData.status,
					department: updateUserData.department || undefined,
					position: updateUserData.position || undefined,
					phone: updateUserData.phone || undefined,
					permissions: updateUserData.permissions,
				};

				const response = await usersApi.update(updateUserData.id, updateData);
				if (response.success) {
					notify.success("Đã cập nhật người dùng thành công");
				} else {
					notify.error(response.message || "Lỗi khi cập nhật người dùng");
				}
			}
			setIsDialogOpen(false);
			await fetchList();
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error!.message);
			}
		}
	}

	const createUser = async (data: UserSchemaType) => {
		try {
			const createData: CreateUserRequest = {
				name: data.name,
				email: data.email,
				username: data.username || undefined,
				role: data.role,
				department: data.department || undefined,
				position: data.position || undefined,
				phone: data.phone || undefined,
				permissions: data.permissions,
				sendWelcomeEmail: true,
			};

			const response = await usersApi.create(createData);
			if (response.success) {
				notify.success("Đã thêm người dùng mới thành công");
			} else {
				notify.error(response.message || "Lỗi khi thêm người dùng");
			}

			setIsDialogOpen(false);
			await fetchList();
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error!.message);
			}
		}
	};

	const getRoleLabel = (role: string) => {
		switch (role) {
			case "admin":
				return "Quản trị viên";
			case "user":
				return "Người dùng";
			case "viewer":
				return "Xem";
			default:
				return role;
		}
	};

	const getRoleBadgeVariant = (role: string) => {
		switch (role) {
			case "admin":
				return "default";
			case "user":
				return "secondary";
			case "viewer":
				return "outline";
			default:
				return "outline";
		}
	};

	const getActivePermissionsCount = (permissions: string[]) => {
		return permissions.length;
	};

	return (
		<div className='space-y-6'>
			<div className='flex items-center justify-between'>
				<div>
					<h2 className='text-2xl mb-2'>Quản Lý Người Dùng</h2>
				</div>
				<div className="flex gap-2">
					<Button
						variant='outline'
						onClick={() => fetchList()}
						disabled={loading}
					>
						<RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
						Làm mới
					</Button>
					<Button variant={"add"} size={'sm'} onClick={handleAdd}>
						<Plus className='w-4 h-4' />
						Thêm người dùng
					</Button>
				</div>
			</div>

			<Card className="gap-2">
				<CardHeader>
					<div className='flex items-center justify-between'>
						<CardTitle>Danh Sách Người Dùng</CardTitle>
						<div className='flex items-center gap-2'>
							<NgocInput value={searchTerm} onChange={(e) => handleFilterChange(setSearchTerm, e.target.value)} className="min-w-96" maxLength={100} placeholder='Tìm kiếm theo tên, email' />
							<Select value={roleFilter} onValueChange={(value) => handleFilterChange(setRoleFilter, value)}>
								<SelectTrigger className='w-40'>
									<SelectValue placeholder='Vai trò' />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value='all'>Tất cả vai trò</SelectItem>
									<SelectItem value='admin'>Quản trị viên</SelectItem>
									<SelectItem value='user'>Người dùng</SelectItem>
									<SelectItem value='viewer'>Xem</SelectItem>
								</SelectContent>
							</Select>
							<Select value={statusFilter} onValueChange={(value) => handleFilterChange(setStatusFilter, value)}>
								<SelectTrigger className='w-40'>
									<SelectValue placeholder='Trạng thái' />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value='all'>Tất cả trạng thái</SelectItem>
									<SelectItem value='active'>Hoạt động</SelectItem>
									<SelectItem value='inactive'>Không hoạt động</SelectItem>
									<SelectItem value='suspended'>Bị đình chỉ</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
				</CardHeader>
				<CardContent>
					<Select value={itemsPerPage.toString()} onValueChange={(value) => {
						setItemsPerPage(Number(value));
						fetchList();
						setCurrentPage(1);
					}}>
						<SelectTrigger className='w-20'>
							<SelectValue placeholder='Hình thức đào tạo' />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='10'>10</SelectItem>
							<SelectItem value='15'>15</SelectItem>
							<SelectItem value='20'>20</SelectItem>
							<SelectItem value='50'>50</SelectItem>
						</SelectContent>
					</Select>

					<div className='relative rounded-md border mt-2'>
						<SmoothLoadingWrapper loading={loading} >
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead className='w-[400px]'>Họ tên</TableHead>
										<TableHead className='w-[100px]'>Email</TableHead>
										<TableHead className='w-[150px]'>Phòng ban</TableHead>
										<TableHead>Quyền</TableHead>
										<TableHead>Permissions</TableHead>
										<TableHead>Trạng thái</TableHead>
										<TableHead>Tình trạng đăng nhập</TableHead>
										<TableHead>Thao tác</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{
										users.length === 0 ?
											<TableRow>
												<TableCell colSpan={11} className='text-center py-8 text-muted-foreground'>
													{searchTerm ? "Không tìm thấy kết quả phù hợp" : "Không có dữ liệu"}
												</TableCell>
											</TableRow>
											:
											users.map((user) => (
												<TableRow key={user.id}>
													<TableCell>
														<div>
															<div className='font-medium' style={{ whiteSpace: 'normal' }}>{user.name}</div>
															{user.username && <div className='text-sm text-muted-foreground' style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{user.username}</div>}
														</div>
													</TableCell>
													<TableCell>
														<div className='text-sm'>{user.email}</div>
														{user.position && <div className='text-xs text-muted-foreground'>{user.position}</div>}
													</TableCell>
													<TableCell>
														<div className='text-sm' style={{ whiteSpace: 'normal' }}>{user.department || ""}</div>
													</TableCell>
													<TableCell>
														{
															user.role &&
															<Badge variant={getRoleBadgeVariant(user.role) as any}>{getRoleLabel(user.role)}</Badge>
														}
													</TableCell>
													<TableCell>
														<Badge variant='secondary'>{getActivePermissionsCount(user.permissions)} quyền</Badge>
													</TableCell>
													<TableCell>
														<Badge
															variant={user.status === "active" ? "add" : user.status === "suspended" ? "destructive" : "secondary"}
															className='cursor-pointer'
														// onClick={() => handleStatusToggle(user.id, user.status)}
														>
															{user.status === "active" ? "Hoạt động" : user.status === "suspended" ? "Bị đình chỉ" : "Không hoạt động"}
														</Badge>
													</TableCell>
													<TableCell>
														<Badge
															variant={user.isLogin ? "add" : "secondary"}
															className='cursor-pointer'
														>
															{user.isLogin ? "Đã đăng nhập" : "Chưa đăng nhập"}
														</Badge>
													</TableCell>
													<TableCell>
														<div className='flex items-center gap-2'>
															{canUpdate &&
																<Button variant='ghost' onClick={() => handleUpdate(user)} className='h-8 w-8 p-0'>
																	<Edit className='w-4 h-4' />
																</Button>
															}
															{canDelete &&
																<Button variant='ghost' onClick={() => setDeleteItemId(user.id)} disabled={user.role === "admin"} className='h-8 w-8 p-0 text-destructive hover:text-destructive'>
																	<Trash2 className='w-4 h-4' />
																</Button>
															}
															{/* <Button variant='ghost' className='h-8 w-8 p-0'>
														<Eye className='w-4 h-4' />
													</Button> */}
														</div>
													</TableCell>
												</TableRow>
											))}
								</TableBody>
							</Table>
						</SmoothLoadingWrapper>
					</div>
					{/* Pagination */}
					{totalPages > 1 && <PaginationIndex setCurrentPage={setCurrentPage} currentPage={currentPage} itemsPerPage={itemsPerPage} totalCount={totalCount} totalPages={totalPages} />}
				</CardContent>
			</Card>
			{
				canCreate &&
				<UserModal isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} editData={editUserData} mode={handleMode} onAdd={createUser} onUpdate={updateUser} />
			}
			<AlertDialog open={deleteItemId !== null} onOpenChange={() => setDeleteItemId(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Xác nhận xóa</AlertDialogTitle>
						<AlertDialogDescription>Bạn có chắc chắn muốn xóa?</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Hủy</AlertDialogCancel>
						<AlertDialogAction onClick={handleDelete} className='bg-destructive text-destructive-foreground hover:bg-destructive/90'>
							Xóa
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
